/**
 * AppHeader Component
 * Sticky MUI AppBar with wordmark (left), page title (centre), crisis alert
 * button and user avatar menu (right). Hamburger icon opens mobile drawer.
 *
 * Desktop (md+): offset to the right of the 300px persistent sidebar.
 * Mobile (<md):  full-width with hamburger that triggers the sidebar drawer
 *                managed by the parent AppLayout.
 *
 * The /chat route does NOT use AppHeader — it renders its own full layout.
 * The /onboarding route does NOT use AppHeader — it is a standalone flow.
 *
 * Accessibility: WCAG 2.1 AA
 * - hamburger: aria-label="Open navigation menu"
 * - crisis button: aria-label="Crisis resources — get urgent support"
 * - avatar button: aria-label="Account menu", aria-haspopup="true"
 * - header landmark: role="banner" (via component="header")
 */

import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  Box,
  Chip,
} from '@mui/material';
import { Menu as MenuIcon, WarningAmber, NotificationsNone, Notifications, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { listNotifications, markRead, dismissNotification } from '../services/notificationService';

/**
 * Route-to-title mapping for all authenticated page routes.
 * Falls back to 'AfterService' for unknown routes.
 */
const ROUTE_TITLES = {
  '/':                  'Dashboard',
  '/chat':              'Digital assistant',
  '/dashboard':         'Dashboard',
  '/profile':           'Profile',
  '/connections':       'Connections',
  '/tools':             'Tools',
  '/directory':         'Service Directory',
  '/eligibility':       'Eligibility Checker',
  '/entitlements':      'Entitlements',
  '/claims':            'Claims',
  '/mental-health':     'Mental Health',
  '/appeals':           'Appeals',
  '/register-provider': 'Register Provider',
  '/providers':         'Provider Directory',
  '/find-support':      'Find Support',
  '/my-orgs':           'My Organisations',
  '/insights':          'Insights',
  '/notifications':     'Notifications',
  '/transition':        'Transition Timeline',
  '/documents':         'Document Vault',
  '/events':            'Events',
  '/help':              'Help & FAQ',
  '/settings':          'Settings',
  '/crisis':            'Crisis Support',
  '/advocate':          'Assistant',
  '/assistant':         'Assistant',
};

/**
 * Sidebar nav group each route belongs to — drives the two-segment breadcrumb
 * (e.g. "Tools / My Claims") in the dark command bar, mirroring the v1.1 mockup.
 */
const ROUTE_GROUPS = {
  '/':              'My Service',
  '/dashboard':     'My Service',
  '/profile':       'My Service',
  '/documents':     'My Service',
  '/transition':    'My Service',
  '/notifications': 'My Service',
  '/entitlements':  'Tools',
  '/claims':        'Tools',
  '/appeals':       'Tools',
  '/eligibility':   'Tools',
  '/mental-health': 'Support',
  '/crisis':        'Support',
  '/events':        'Community',
  '/directory':     'Community',
  '/providers':     'Community',
  '/find-support':  'Community',
  '/my-orgs':       'Community',
  '/advocate':      'Digital assistant',
  '/assistant':     'Digital assistant',
  '/chat':          'Digital assistant',
};

/**
 * AppHeader — sticky header bar for authenticated page routes.
 *
 * @param {{ user: object, onMobileDrawerToggle: function }} props
 */
export default function AppHeader({ user, onMobileDrawerToggle, sessions = [], sidebarOpen = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Notification bell state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellAnchorEl, setBellAnchorEl] = useState(null);
  const bellOpen = Boolean(bellAnchorEl);

  const pageTitle = ROUTE_TITLES[location.pathname] ?? 'AfterService';
  const pageGroup = ROUTE_GROUPS[location.pathname] ?? null;

  // Derive user initials for avatar
  const initials = (
    (user?.given_name?.[0] ?? '') + (user?.family_name?.[0] ?? '')
  ).toUpperCase() || user?.email?.[0]?.toUpperCase() || 'A';

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewProfile = () => {
    navigate('/profile');
    handleMenuClose();
  };

  const handleSignOut = async () => {
    handleMenuClose();
    try {
      await signOut();
    } catch (err) {
      // signOut failure is non-fatal
    } finally {
      window.location.href = '/';
    }
  };

  // 60-second polling for notifications
  useEffect(() => {
    let active = true;
    const fetchNotifs = async () => {
      try {
        const data = await listNotifications();
        if (active) {
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (_err) {
        // Non-fatal — notification failure must not block the app
      }
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  const handleBellClick = (event) => { setBellAnchorEl(event.currentTarget); };
  const handleBellClose = () => { setBellAnchorEl(null); };

  const handleNotifClick = async (notif) => {
    try { await markRead(notif.notificationId); } catch (_) {}
    setNotifications(prev => prev.map(n => n.notificationId === notif.notificationId ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - (notif.read ? 0 : 1)));
    handleBellClose();
    // 400ms delay: DynamoDB UserIndex GSI is eventually consistent — give it time
    // to replicate the mark-read update before the target page queries it.
    await new Promise(r => setTimeout(r, 400));
    if (notif.relatedEntityType === 'appeal') navigate('/appeals');
    else if (notif.relatedEntityType === 'claim') navigate('/claims');
    else navigate('/notifications');
  };

  const handleNotifDismiss = async (e, notifId) => {
    e.stopPropagation();
    const target = notifications.find(n => n.notificationId === notifId);
    try { await dismissNotification(notifId); } catch (_) {}
    setNotifications(prev => prev.filter(n => n.notificationId !== notifId));
    if (target && !target.read) setUnreadCount(prev => Math.max(0, prev - 1));
  };

  return (
    <AppBar
      component="header"
      position="fixed"
      role="banner"
      elevation={1}
      sx={{
        // v1.1: full-width command bar across the very top — sits ABOVE the
        // sidebar (which starts below the 64px header). Logo leads top-left.
        zIndex: (theme) => theme.zIndex.drawer + 1,
        left: 0,
        right: 0,
        width: '100%',
        // dark command bar (ink) with a 1px brand accent underrunning it.
        bgcolor: 'var(--as-ink)',
        color: 'var(--as-d1)',
        borderBottom: '1px solid var(--as-brand)',
      }}
    >
      <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>

        {/* Hamburger — MOBILE ONLY (opens the nav drawer). Per the design the
            desktop header has no menu button; the rail's own collapse control
            handles desktop. */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="Open navigation menu"
          onClick={onMobileDrawerToggle}
          sx={{ mr: 1, color: 'var(--as-d1)', display: { md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        {/* Wordmark cell — fixed-width column that mirrors the sidebar width so
            the breadcrumb that follows starts exactly at the main-content left
            edge. Width tracks the sidebar (300px expanded / 64px collapsed, less
            the 16px toolbar gutter). When collapsed the cell is too narrow for
            the full wordmark, so it shows the chevron symbol only — matching the
            collapsed mini-rail. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            overflow: 'hidden',
            width: { xs: 'auto', md: sidebarOpen ? 'calc(300px - 16px)' : 'calc(64px - 16px)' },
            transition: 'width 0.225s cubic-bezier(0.4,0,0.6,1)',
          }}
        >
          <Box
            component="img"
            src={sidebarOpen ? '/afterservice-logo-reversed.svg?v=20260608' : '/afterservice-symbol-reversed.svg?v=20260608'}
            alt="AfterService"
            sx={{ height: 42, width: 'auto', flexShrink: 0, display: 'block' }}
          />
        </Box>

        {/* Breadcrumb — left-aligned to the main-content edge. The fixed-width
            wordmark cell above spans the sidebar column, so this begins exactly
            at the content column's left edge.
            Two-segment mono: muted GROUP / brighter PAGE (e.g. "AI ASSISTANT / ASSISTANT"). */}
        <Typography
          variant="h6"
          component="span"
          noWrap
          sx={{
            flexShrink: 0,
            minWidth: 0,
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'var(--as-font-mono)',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            display: { xs: 'none', md: 'block' },
          }}
        >
          {pageGroup && (
            <Box component="span" sx={{ color: 'var(--as-d3)' }}>
              {pageGroup}{' '}<Box component="span" sx={{ opacity: 0.5 }}>/</Box>{' '}
            </Box>
          )}
          <Box component="span" sx={{ color: 'var(--as-d1)' }}>{pageTitle}</Box>
        </Typography>

        {/* Beta chip — only on /assistant route; trails the breadcrumb. */}
        {location.pathname === '/assistant' && (
          <Chip
            label="Beta"
            size="small"
            sx={{
              ml: 1.5,
              height: 22,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: '#ffffff',
              border: '1px solid rgba(255,255,255,0.4)',
            }}
          />
        )}

        {/* Spacer — pushes the action icons to the right edge. */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Crisis alert button — always visible */}
        <Tooltip title="Get urgent support">
          <IconButton
            aria-label="Crisis resources — get urgent support"
            onClick={() => navigate('/crisis')}
            sx={{ color: '#ef4444', flexShrink: 0 }}
          >
            <WarningAmber />
          </IconButton>
        </Tooltip>

        {/* Notification bell */}
        <Tooltip title={unreadCount > 0 ? `${unreadCount} unread` : 'Notifications'}>
          <IconButton
            aria-label={`Notifications — ${unreadCount} unread`}
            aria-haspopup="true"
            aria-controls={bellOpen ? 'notifications-popover' : undefined}
            aria-expanded={bellOpen ? 'true' : undefined}
            onClick={handleBellClick}
            sx={{ color: 'inherit', flexShrink: 0 }}
          >
            {unreadCount > 0 ? (
              <Badge badgeContent={unreadCount} color="error">
                <Notifications />
              </Badge>
            ) : (
              <NotificationsNone />
            )}
          </IconButton>
        </Tooltip>

        {/* User avatar — opens account menu */}
        <IconButton
          aria-label="Account menu"
          aria-controls={menuOpen ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? 'true' : undefined}
          onClick={handleAvatarClick}
          sx={{ p: 0.75 }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'var(--as-blue-500)',
              color: '#ffffff',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {initials}
          </Avatar>
        </IconButton>

        <Menu
          id="account-menu"
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleViewProfile}>View Profile</MenuItem>
          <Divider />
          <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
        </Menu>

        {/* Notifications popover */}
        <Popover
          id="notifications-popover"
          open={bellOpen}
          anchorEl={bellAnchorEl}
          onClose={handleBellClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 360, maxHeight: 440, overflow: 'auto' } }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="subtitle2" fontWeight={600}>Notifications</Typography>
            <Button size="small" onClick={() => { handleBellClose(); navigate('/notifications'); }} sx={{ fontSize: 12 }}>View all</Button>
          </Box>
          {notifications.filter(n => !n.dismissed).slice(0, 5).length === 0 ? (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No notifications</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.filter(n => !n.dismissed).slice(0, 5).map(notif => (
                <ListItem
                  key={notif.notificationId}
                  disablePadding
                  secondaryAction={
                    <IconButton size="small" aria-label="Dismiss notification" onClick={(e) => handleNotifDismiss(e, notif.notificationId)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                  sx={{ backgroundColor: notif.read ? 'transparent' : 'rgba(80,134,237,0.07)', borderLeft: notif.read ? 'none' : '3px solid var(--as-blue-500)' }}
                >
                  <ListItemButton onClick={() => handleNotifClick(notif)} sx={{ pr: 5 }}>
                    <ListItemText
                      primary={notif.title}
                      secondary={notif.body}
                      primaryTypographyProps={{ fontWeight: notif.read ? 400 : 600, fontSize: 13 }}
                      secondaryTypographyProps={{ fontSize: 12, sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Popover>

      </Toolbar>
    </AppBar>
  );
}
